import { roundNumber } from '@/lib/formatUtils';

export type QuickMathOperator = '+' | '-' | '*' | '/';

export type QuickMathResult =
  | { ok: true; value: number }
  | { ok: false; reason: 'invalid_base' | 'invalid_operand' | 'division_by_zero' };

const QUICK_MATH_OPERATORS: ReadonlySet<string> = new Set(['+', '-', '*', '/']);

function normalizeQuickMathNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const sanitizedValue = value.replace(/[^\d.,-]/g, '').replace(/,/g, '');

  if (!sanitizedValue) {
    return null;
  }

  const parsedValue = Number.parseFloat(sanitizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

export function isQuickMathOperator(value: string): value is QuickMathOperator {
  return QUICK_MATH_OPERATORS.has(value);
}

export function parseQuickMathOperand(value: string): number | null {
  return normalizeQuickMathNumber(value);
}

export function canStartQuickMath(value: unknown): boolean {
  return normalizeQuickMathNumber(value) !== null;
}

export function resolveQuickMathBaseValue(value: unknown): number | null {
  return normalizeQuickMathNumber(value);
}

export function applyQuickMath({
  baseValue,
  operator,
  operand
}: {
  baseValue: number;
  operator: QuickMathOperator;
  operand: number;
}): QuickMathResult {
  if (!Number.isFinite(baseValue)) {
    return { ok: false, reason: 'invalid_base' };
  }

  if (!Number.isFinite(operand)) {
    return { ok: false, reason: 'invalid_operand' };
  }

  if (operator === '/' && operand === 0) {
    return { ok: false, reason: 'division_by_zero' };
  }

  switch (operator) {
    case '+':
      return { ok: true, value: roundNumber(baseValue + operand, 6) };
    case '-':
      return { ok: true, value: roundNumber(baseValue - operand, 6) };
    case '*':
      return { ok: true, value: roundNumber(baseValue * operand, 6) };
    case '/':
      return { ok: true, value: roundNumber(baseValue / operand, 6) };
  }
}
